/**
 * Cómo se escribe la plata y las fechas en toda la interfaz.
 *
 * Antes de este módulo había **cinco copias** de la misma función `pesos()`
 * —en `presupuestos/consulta.ts`, `RenglonConsulta.tsx`, `produccion/orden.ts`,
 * `productos/producto.ts` y `InicioPagina.tsx`— y, peor, **cuatro pantallas que
 * no formateaban nada**: Lista de Precios mostraba `1850.0000`, el listado de
 * Consultas `21978.00`, Reportes `63800.00` y Facturación un guion donde va el
 * total.
 *
 * El `1850.0000` no era un descuido de formato: es el `numeric` de Postgres
 * llegando como string y pintándose tal cual. Es el mismo problema que resolvió
 * el ADR 0085 con los códigos de la base — el dato crudo no es lo que se muestra.
 */

/**
 * Importe en pesos, con el separador de miles y los dos decimales de es-AR:
 * `$1.850,00`.
 *
 * Acepta `string` porque el worker manda los `numeric` de Postgres como texto
 * (`"1850.0000"`), y ese es justamente el caso que se veía roto.
 *
 * `null`, `undefined` y lo que no sea un número devuelven **`—`**: en un listado
 * la ausencia de importe es un dato —«todavía no se puede calcular»— y no un
 * cero, que sería mentira.
 */
export function pesos(valor: number | string | null | undefined): string {
  // `Number("")` es 0, no NaN: sin este corte un importe vacío se mostraría
  // como `$0,00`, que es justo la mentira que este módulo evita.
  if (typeof valor === "string" && valor.trim() === "") return "—";
  const n = typeof valor === "string" ? Number(valor) : valor;
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  return `$${n.toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Precio **por unidad**, con dos decimales como mínimo y **hasta cuatro**:
 * `$1.850,00`, pero también `$0,1234`.
 *
 * No es lo mismo que un importe. El precio unitario de una tirada grande lleva
 * fracciones de centavo, y redondear `$0,1234` a `$0,12` corre la cuenta $34
 * sobre 10.000 unidades. Un **total** no tiene ese problema: se cobra en pesos
 * y va con `pesos()`.
 */
export function precioUnitario(valor: number | string | null | undefined): string {
  if (typeof valor === "string" && valor.trim() === "") return "—";
  const n = typeof valor === "string" ? Number(valor) : valor;
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  return `$${n.toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  })}`;
}

/**
 * Cantidad sin unidad monetaria, con los mismos separadores: `1.850` o `1.850,5`.
 *
 * Para lo que se cuenta y no se cobra —órdenes, eventos, unidades—. Sin
 * decimales forzados: `250` se lee `250`, no `250,00`.
 */
export function numero(valor: number | string | null | undefined): string {
  if (typeof valor === "string" && valor.trim() === "") return "—";
  const n = typeof valor === "string" ? Number(valor) : valor;
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  return n.toLocaleString("es-AR", { maximumFractionDigits: 2 });
}

/** Una fecha ISO (`2026-08-19` o con hora) sin la trampa del huso horario. */
function aFecha(valor: string | Date | null | undefined): Date | null {
  if (valor === null || valor === undefined || valor === "") return null;
  if (valor instanceof Date) return Number.isNaN(valor.getTime()) ? null : valor;
  // `2026-08-19` lo parsea el motor como UTC: en Argentina eso es el 18 a las
  // 21 h, y la fecha se muestra un día antes. Con las partes explícitas, no.
  const soloFecha = /^(\d{4})-(\d{2})-(\d{2})$/.exec(valor);
  if (soloFecha) {
    const [, a, m, d] = soloFecha;
    return new Date(Number(a), Number(m) - 1, Number(d));
  }
  const d = new Date(valor);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Fecha en el formato que usa `imprenta` y que se lee en Argentina:
 * **`19/08/2026`**. Nunca el ISO `2026-08-19`, que es formato de base.
 */
export function fecha(valor: string | Date | null | undefined): string {
  const d = aFecha(valor);
  if (d === null) return "—";
  return d.toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/**
 * Fecha y hora: **`19/08/2026, 07:15 p. m.`**
 *
 * En 12 horas y **sin segundos**, como `imprenta`. El día va con cero adelante:
 * `1/9/2026` y `01/09/2026` en la misma columna desalinean la lectura.
 */
export function fechaHora(valor: string | Date | null | undefined): string {
  const d = aFecha(valor);
  if (d === null) return "—";
  return `${d.toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  })}, ${d.toLocaleTimeString("es-AR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  })}`;
}

/**
 * Qué parte del total es esto: **`34,2%`**
 *
 * Una columna de conteos no contesta «¿esto es mucho?»: 47 órdenes en un estado
 * es mucho o poco según cuántas haya. `imprenta` lo muestra al lado del número
 * en Producción y en Consultas, y acá se había perdido.
 *
 * **Con total en cero devuelve `—`, no `0%`.** No es lo mismo «ninguna de 200»
 * que «no hay ninguna»: dividir por cero da `NaN`, y forzarlo a `0%` inventaría
 * una proporción sobre un conjunto vacío.
 *
 * Un decimal: con dos, la columna se llena de ruido que nadie compara.
 */
export function porcentaje(parte: number, total: number): string {
  if (!Number.isFinite(parte) || !Number.isFinite(total) || total === 0) return "—";
  return `${((parte / total) * 100).toLocaleString("es-AR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
}
